import { useId, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { addYears, format } from 'date-fns';
import {
	ChevronDown,
	Download,
	FileText,
	Image,
	ImageIcon,
	Info,
	Loader2,
	Trash2,
	UploadCloud
} from 'lucide-react';
import { type DropzoneState, useDropzone } from 'react-dropzone';
import {
	type FieldArrayWithId,
	useFieldArray,
	useFormContext
} from 'react-hook-form';

import DateControl from '@/components/form/date-control';
import InputControl from '@/components/form/input-control';
import InputTextAreaControl from '@/components/form/input-text-area-control';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import PreviewFileDialog from '@/components/ui/preview-file';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { WithStateQuery } from '@/components/ui/with-state';
import { StatusDocument } from '@/constants/status';
import { useConfirmation } from '@/hooks/use-confirm-dialog';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileMutations } from '@/queries/file/file.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { noOp } from '@/services/fn';
import { extractFileParts, onFileRejected } from '@/services/string';
import { useDialogStore } from '@/store';
import {
	type ApproveDocumentSchema,
	approveDocumentSchema,
	type DocumentDataSchema,
	documentDataSchema
} from '@/types/form/account-manager/form';
import {
	DOCUMENT,
	type DocumentId,
	type VendorDocumentDataResponse
} from '@/types/vendor/vendor';

function RejectOption() {
	const { watch } = useFormContext<ApproveDocumentSchema>();
	const [type] = watch(['option']);

	return (
		<div className="flex flex-col gap-2 border-b border-b-gray-200 pb-2">
			<FormField
				name="option"
				render={({ field: { onChange, value } }) => (
					<FormItem className={'flex w-full flex-col gap-2'}>
						<FormLabel htmlFor={'option'} className="font-bold">
							Pilih Alasan Penolakan
						</FormLabel>
						<FormControl>
							<RadioGroup
								id="option"
								value={value}
								onValueChange={onChange}
								className="flex flex-col"
							>
								<div className="flex space-x-3">
									<RadioGroupItem value={'Dokumen Tidak Lengkap'} id={'opt-1'} />
									<Label className="text-slate-600" htmlFor={'opt-1'}>
										Dokumen Tidak Lengkap
									</Label>
								</div>
								<div className="flex space-x-3">
									<RadioGroupItem value={'Kualitas Visual Dokumen Buruk'} id={'opt-2'} />
									<Label className="text-slate-600" htmlFor={'opt-2'}>
										Kualitas Visual Dokumen Buruk
									</Label>
								</div>
								<div className="flex space-x-3">
									<RadioGroupItem
										value={'Tidak Ada Tanda Tangan / Stempel Resmi'}
										id={'opt-3'}
									/>
									<Label className="text-slate-600" htmlFor={'opt-3'}>
										Tidak Ada Tanda Tangan / Stempel Resmi
									</Label>
								</div>
								<div className="flex space-x-3">
									<RadioGroupItem
										value={'Data Tidak Akurat & Kontradiktif'}
										id={'opt-4'}
									/>
									<Label className="text-slate-600" htmlFor={'opt-4'}>
										Data Tidak Akurat & Kontradiktif
									</Label>
								</div>
								<div className="flex space-x-3">
									<RadioGroupItem value={'Informasi Kedaluwarsa'} id={'opt-5'} />
									<Label className="text-slate-600" htmlFor={'opt-5'}>
										Informasi Kedaluwarsa
									</Label>
								</div>
								<div className="flex space-x-3">
									<RadioGroupItem value={'other'} id={'opt-6'} />
									<Label className="text-slate-600" htmlFor={'opt-6'}>
										Lainnya
									</Label>
								</div>
							</RadioGroup>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			{type === 'other' ? (
				<InputTextAreaControl
					rows={4}
					name="remarks"
					label="Alasan Penolakan"
					placeholder="Masukkan Alasan Lainnya"
				/>
			) : null}
		</div>
	);
}

function ReviseDocument({
	vendorDocumentId,
	vendorId,
	next
}: Readonly<{ vendorDocumentId: number, vendorId: number, next: () => void }>) {
	const formId = useId();
	const { onSuccess, onError } = useResponse();

	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const approve = useMutation(
		vendorMutations.approveDocument(vendorDocumentId, vendorId.toString())
	);

	function handleSubmit(data: ApproveDocumentSchema) {
		setDialog('variant', 'warning');
		setDialog('title', 'Tolak Dokumen?');
		setDialog(
			'description',
			'Vendor akan melakukan revisi dan mengirimkan kembali dokumen yang Anda tolak'
		);
		setDialog('confirmText', 'Ya, Lanjutkan');
		setDialog('cancelText', 'Batal');
		setAction('onConfirm', () => {
			approve.mutate(
				{
					status: 'revised',
					remarks: data.option === 'other' ? data.remarks : data.option
				},
				{
					onSuccess: () => {
						onSuccess({ message: 'Dokumen Berhasil Ditolak' });
						setAction('onConfirm', () => {
							next();
							onOpen(false);
						});
					},
					onError: ({ message }) => onError({ message })
				}
			);
		});
		setDialog('open', true);
	}
	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button
					variant={'outline'}
					className="border-destructive text-destructive hover:text-destructive/70 hover:border-destructive/80"
				>
					Tolak
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader className="border-b border-b-gray-200 pb-2">
					<DialogTitle>Tolak Dokumen</DialogTitle>
					<DialogDescription>
						Pilih atau masukkan alasan penolakan dokumen Vendor
					</DialogDescription>
				</DialogHeader>
				<HookFormProvider
					schema={approveDocumentSchema}
					onSubmit={handleSubmit}
					defaultValues={{
						status: 'revised'
					}}
					id={formId}
				>
					<RejectOption />
				</HookFormProvider>
				<DialogFooter className="flex shrink-0 flex-row gap-2 sm:justify-end">
					<Button disabled={approve.isPending} form={formId}>
						Kirim
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
function ApprovalDocument({
	vendorDocumentId,
	vendorId,
	next
}: Readonly<{ vendorDocumentId: number, vendorId: number, next: () => void }>) {
	const { onSuccess, onError } = useResponse();

	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const approve = useMutation(
		vendorMutations.approveDocument(vendorDocumentId, vendorId.toString())
	);

	return (
		<>
			<DialogFooter className="flex shrink-0 flex-row gap-2 sm:justify-end">
				<ReviseDocument
					vendorDocumentId={vendorDocumentId}
					vendorId={vendorId}
					next={next}
				/>
				<Button
					type="button"
					disabled={approve.isPending}
					onClick={() => {
						setDialog('variant', 'warning');
						setDialog('title', 'Setujui?');
						setDialog(
							'description',
							'Apakah Anda yakin ingin menyetujui dokumen vendor ini?'
						);
						setDialog('confirmText', 'Ya, Setujui');
						setDialog('cancelText', 'Batal');
						setAction('onConfirm', () => {
							approve.mutate(
								{
									status: 'approved'
								},
								{
									onSuccess: () => {
										onSuccess({ message: 'Dokumen Berhasil Disetujui' });
										setAction('onConfirm', () => {
											next();
											onOpen(false);
										});
									},
									onError: ({ message }) => onError({ message })
								}
							);
						});
						setDialog('open', true);
					}}
				>
					Setujui
				</Button>
			</DialogFooter>
			<div />
		</>
	);
}

function DownloadTemplateDocument({
	documentId
}: Readonly<{ documentId: DocumentId }>) {
	const templateStatementOfInterest = useMutation(
		vendorMutations.templateStatementOfInterest
	);
	const templateStatementAuthenticity = useMutation(
		vendorMutations.templateStatementAuthenticity
	);
	return (
		<Button
			type="button"
			variant={'outline'}
			onClick={() => {
				if (documentId === 'D008') {
					return templateStatementOfInterest.mutate();
				}

				templateStatementAuthenticity.mutate();
			}}
		>
			<Download />
			Unduh Format
		</Button>
	);
}

function FileUpload({
	vendorName,
	documentId,
	isDisabled,
	formId,
	name,
	description,
	visible,
	onOpenChange,
	files,
	allowApproval,
	vendorDocumentId,
	vendorId,
	defaultValues
}: Readonly<{
	vendorName: string,
	documentId: DocumentId,
	isDisabled: boolean,
	formId: string,
	name: string,
	visible: boolean,
	onOpenChange: (open: boolean) => void,
	description: string | null,
	files: VendorDocumentDataResponse['files'],
	allowApproval: boolean,
	vendorDocumentId: number,
	vendorId: number,
	defaultValues: DocumentDataSchema
}>) {
	const document = DOCUMENT[documentId];

	const {
		control,
		reset,
		formState: { isDirty }
	} = useFormContext<DocumentDataSchema>();
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'files'
	});
	const hasReachMaxFiles = fields.length === document.max;

	const [progress, setProgress] = useState<number>(0);
	const onOpen = useDialogStore((s) => s.onOpenChange);
	const { askCloseIfDirty, askConfirmDeleteDocument } = useConfirmation();
	const { onError } = useResponse();

	const uploadMutation = useMutation(fileMutations.uploadPresign);

	const dropzone = useDropzone({
		accept:
			document.type === 'image'
				? {
						'image/png': ['.png'],
						'image/jpeg': ['.jpg', '.jpeg']
					}
				: { 'application/pdf': ['.pdf'] },
		disabled: uploadMutation.isPending || isDisabled || hasReachMaxFiles,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setProgress(0);

			const date = new Date();
			const { fileName, fileExt } = extractFileParts(file.name);
			const fileKey = `${vendorName}/${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${crypto.randomUUID()}/${fileName.slice(0, 50)}.${fileExt}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: () => {
						append({
							fileName: fileKey,
							startDate: null,
							endDate: null,
							documentNumber: null
						});
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({
					code: errFile.errors[0]?.code,
					type: document.type
				}),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const documentDescription =
		document.type === 'image'
			? 'Dokumen: JPG, JPEG, PNG (Maks. 5MB per file) dan Unggah satu atau lebih gambar'
			: 'Dokumen: Pdf (Maks. 5MB per file) dan Unggah satu atau lebih dokumen';

	const type = document.type === 'pdf' ? 'Unggah Dokumen' : 'Unggah Gambar';

	const buttonText = () => {
		if (isDisabled) {
			return 'Lihat Dokumen';
		}

		if (files.length > 0) {
			return 'Lihat atau unggah ulang dokumen';
		}

		return documentDescription;
	};

	return (
		<Dialog
			open={visible}
			onOpenChange={(v) => {
				if (!v) {
					return askCloseIfDirty(
						isDirty,
						() => {},
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					);
				}

				onOpenChange(true);
			}}
		>
			<div className="mt-2 flex w-full items-center justify-between gap-2">
				<DialogTrigger asChild>
					<div className="flex flex-col gap-1">
						<Button
							variant={'outline'}
							className="border-input flex h-auto min-h-12 w-full max-w-fit flex-1 items-start justify-start py-2 text-start"
							type="button"
						>
							<img
								src="/icon/upload.webp"
								alt="icon-upload"
								className="mt-0.5 size-4 shrink-0"
							/>
							<span className="flex min-w-0 flex-col items-start justify-start">
								<p className="text-xs text-black">
									{files.length > 0 || allowApproval || isDisabled
										? `Lihat Dokumen (${files.length})`
										: type}
								</p>
								<p className="text-secondary items-star flex justify-start text-justify text-xs leading-tight font-light whitespace-normal">
									{buttonText()}
								</p>
							</span>
						</Button>
						{files.length === 0 && document.isRequired(defaultValues.value) ? (
							<p
								data-slot="form-description"
								className={cn('text-destructive text-sm')}
							>
								* Wajib diisi
							</p>
						) : null}
					</div>
				</DialogTrigger>
				{document.format ? (
					<div className="w-fit">
						<DownloadTemplateDocument documentId={documentId} />
					</div>
				) : null}
			</div>
			<DialogContent
				className="flex max-h-[85vh] flex-col bg-white"
				onPointerDownOutside={(e) =>
					askCloseIfDirty(
						isDirty,
						() => e.preventDefault(),
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					)
				}
				onEscapeKeyDown={(e) =>
					askCloseIfDirty(
						isDirty,
						() => e.preventDefault(),
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					)
				}
			>
				<DialogTitle className="flex shrink-0 flex-col gap-1">
					<span className="text-sm font-bold text-black">{name}</span>
					<span className="text-label-input text-xs">{description}</span>
				</DialogTitle>
				<div className="flex flex-1 flex-col gap-2 overflow-y-auto">
					<MultipleSlotUpload
						{...dropzone}
						remove={(index) =>
							askConfirmDeleteDocument(() => {
								remove(index);
								onOpen(false);
							})
						}
						isDisabled={isDisabled}
						document={document}
						isPending={uploadMutation.isPending}
						progress={progress}
						hasReachMaxFiles={hasReachMaxFiles}
						fields={fields.map((f, i) => ({ ...f, originalIndex: i }))}
						label={document.type === 'pdf' ? 'Unggah Dokumen' : 'Unggah Gambar'}
					/>
				</div>

				{isDisabled ? null : (
					<DialogFooter className="shrink-0 sm:justify-end">
						<Button
							type="submit"
							disabled={uploadMutation.isPending || !isDirty}
							form={formId}
						>
							Simpan
						</Button>
					</DialogFooter>
				)}
				{allowApproval ? (
					<ApprovalDocument
						vendorDocumentId={vendorDocumentId}
						vendorId={vendorId}
						next={() => onOpenChange(false)}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function AktaPerubahaan({
	value,
	isDisabled,
	vendorName,
	files,
	description,
	name,
	visible,
	onOpenChange,
	allowApproval,
	isApproval,
	vendorDocumentId,
	vendorId
}: Readonly<{
	value?: string,
	isDisabled: boolean,
	vendorName: string,
	files: VendorDocumentDataResponse['files'],
	name: string,
	description: string | null,
	visible: boolean,
	onOpenChange: (open: boolean) => void,
	allowApproval: boolean,
	isApproval: boolean,
	vendorDocumentId: number,
	vendorId: number
}>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const formId = useId();
	const submit = useMutation(vendorMutations.documentDataUpdate);

	const { onSuccess, onError } = useResponse();

	const handleSubmit = (
		data: StrictOmit<DocumentDataSchema, 'documentId'>,
		type: 'file' | 'option'
	) => {
		submit.mutate(
			{
				...data,
				documentId: 'D001',
				files: data.files.map((file) => ({
					...file,
					startDate: file.startDate ? format(file.startDate, 'yyyy-MM-dd') : null,
					endDate: file.endDate ? format(file.endDate, 'yyyy-MM-dd') : null
				}))
			},
			{
				onSuccess: () => {
					if (type === 'file') {
						onSuccess({ message: 'Dokumen Anda berhasil di unggah!' });
						onOpenChange(false);
					} else {
						onOpen(false);
					}
				},
				onError: ({ message }) => onError({ message })
			}
		);
	};
	return (
		<div className="flex flex-1 grow flex-col items-start justify-between">
			<RadioGroup
				value={value}
				disabled={isDisabled}
				onValueChange={(value) => {
					if (value === 'no' && files.length > 0) {
						setDialog('variant', 'warning');
						setDialog('title', 'Ubah akta perubahan?');
						setDialog(
							'description',
							'Mengubah opsi akan menghapus semua file yang sudah diunggah'
						);
						setDialog('confirmText', 'Ya, Ubah');
						setDialog('cancelText', 'Batal');
						setAction('onConfirm', () =>
							handleSubmit({ files: [], value }, 'option')
						);
						setAction('onCancel', () => onOpen(false));
						onOpen(true);
						return;
					}

					handleSubmit({ files, value }, 'option');
				}}
				className="flex flex-row"
			>
				<div className="flex space-x-2">
					<RadioGroupItem value={'yes'} id={'yes'} />
					<Label htmlFor={'yes'}>Ya, Pernah</Label>
				</div>
				<div className="flex space-x-2">
					<RadioGroupItem value="no" id="no" />
					<Label htmlFor="no">Tidak Pernah</Label>
				</div>
			</RadioGroup>
			{DOCUMENT['D001'].isRequired(value) || allowApproval || isApproval ? (
				<HookFormProvider
					schema={documentDataSchema}
					defaultValues={{
						files,
						documentId: 'D001',
						value
					}}
					onSubmit={(data: DocumentDataSchema) => handleSubmit(data, 'file')}
					id={formId}
					key={formId}
					formOptions={{ disabled: isDisabled }}
				>
					<FileUpload
						documentId="D001"
						vendorName={vendorName}
						isDisabled={isDisabled}
						formId={formId}
						name={name}
						description={description}
						visible={visible}
						onOpenChange={onOpenChange}
						key={formId}
						files={files}
						allowApproval={allowApproval}
						vendorDocumentId={vendorDocumentId}
						vendorId={vendorId}
						defaultValues={{
							files,
							documentId: 'D001',
							value
						}}
					/>
				</HookFormProvider>
			) : null}
		</div>
	);
}

interface SingleSlotProps {
	label: string;
	field?: FieldArrayWithId<DocumentDataSchema> & { originalIndex: number };
	tag: 'SPPKP' | 'SKT';
	onUpload: (file: File, t: SingleSlotProps['tag']) => void;
	onRemove: () => void;
	isDisabled: boolean;
	isPending: boolean;
}

const SingleSlotUpload = ({
	label,
	field,
	onUpload,
	onRemove,
	isDisabled,
	isPending,
	tag
}: SingleSlotProps) => {
	const { onError } = useResponse();

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: { 'application/pdf': ['.pdf'] },
		disabled: isDisabled || isPending,
		maxSize: 5_242_880,
		multiple: false,
		noKeyboard: true,
		noClick: true,
		onDropAccepted: (files) => {
			const file = files[0];
			if (!file) return;

			onUpload(file, tag);
		},
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'pdf' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	if (field) {
		const idx = field.originalIndex;

		return (
			<Card className="rounded-md border p-3">
				<CardTitle className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-5">
					<div className="text-primary flex items-start gap-2">
						<span className="shrink-0 pt-1">
							<FileText size={16} />
						</span>

						<PreviewFileDialog
							fileKey={field.fileName}
							label={label.concat(
								' ',
								String(
									field.fileName
										.split('/')
										.pop()
										?.replace(/^(SPPKP\/|SKT\/)/, '')
								)
							)}
						/>
					</div>
					{isDisabled ? null : (
						<div className="my-auto flex h-1/2 shrink-0 items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								onClick={onRemove}
								className={'size-8 shrink-0'}
							>
								<Trash2 className="text-destructive" size={14} />
							</Button>
						</div>
					)}
				</CardTitle>

				<CardContent className="flex flex-col gap-2 p-1">
					<InputControl
						name={`files.${idx}.documentNumber`}
						placeholder="No. Dokumen"
					/>
					<div className="flex gap-2">
						<DateControl
							name={`files.${idx}.startDate`}
							placeholder="Tanggal Terbit"
							required
							calendarProps={{
								captionLayout: 'dropdown',
								endMonth: addYears(new Date(), 40)
							}}
						/>
						<DateControl
							name={`files.${idx}.endDate`}
							placeholder="Tanggal Akhir"
							required
							calendarProps={{
								captionLayout: 'dropdown',
								endMonth: addYears(new Date(), 40)
							}}
						/>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="flex flex-col items-center justify-center gap-2 border-2 border-dashed bg-gray-50/50 p-4">
			<div className="flex w-full items-center justify-between">
				<span className="text-sm font-medium text-gray-700">{label}</span>
			</div>
			<div {...getRootProps()} className="w-full">
				<input {...getInputProps()} />
				<Button
					type="button"
					variant="outline"
					className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
					onClick={open}
				>
					<UploadCloud className="mr-2 h-4 w-4" />
					Unggah Dokumen
				</Button>
			</div>
		</Card>
	);
};

function MultipleSlotUpload({
	isDragAccept,
	getInputProps,
	getRootProps,
	open,
	isPending,
	progress,
	hasReachMaxFiles = false,
	document,
	fields,
	isDisabled,
	remove,
	label
}: {
	isPending: boolean,
	progress: number,
	hasReachMaxFiles?: boolean,
	document: (typeof DOCUMENT)[keyof typeof DOCUMENT],
	fields: (FieldArrayWithId<DocumentDataSchema> & { originalIndex: number })[],
	remove: (index: number) => void,
	isDisabled: boolean,
	label: string
} & DropzoneState) {
	return (
		<>
			{isDisabled ? null : (
				<div
					{...getRootProps({
						className: cn(
							'dropzone border-2 border-slate-300 border-dashed h-24 flex flex-col gap-1 justify-center items-center p-6 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
							isDragAccept && 'bg-slate-400/50'
						)
					})}
				>
					<input {...getInputProps()} />

					{isPending ? (
						<div className="z-10 flex flex-col items-center gap-2">
							<Loader2 className="size-8 animate-spin text-blue-500" />
							<span className="text-sm font-medium">Uploading... {progress}%</span>
						</div>
					) : null}

					{!isPending && (
						<>
							<div
								className={cn(
									'flex w-full cursor-pointer items-center justify-center rounded bg-transparent p-2 transition hover:bg-gray-50',
									{
										'cursor-default': hasReachMaxFiles
									}
								)}
								onClick={hasReachMaxFiles ? noOp : open}
								role="none"
							>
								{hasReachMaxFiles ? (
									<Info className="size-6 text-gray-400" />
								) : (
									<ImageIcon className="size-6 text-gray-400" />
								)}
							</div>
							<span className="font-medium text-black">
								{hasReachMaxFiles ? 'Batas Upload Dokumen Tercapai' : label}
							</span>
							<span className="text-secondary pb-1 text-xs">
								{hasReachMaxFiles ? (
									'Hapus dokumen yang ada jika Anda ingin mengganti file'
								) : (
									<>
										Unggah file {document.type === 'image' ? 'JPG, JPEG, PNG' : 'PDF'}{' '}
										(maks. 5MB)
									</>
								)}
							</span>
						</>
					)}
				</div>
			)}
			<div className="flex min-h-0 flex-1 flex-col gap-4 pr-2">
				{fields.map((field) => (
					<Card key={field.id} className="rounded-md bg-gray-50 px-2 py-1">
						<Collapsible defaultOpen>
							<CardHeader className="p-1">
								<CardTitle className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-5">
									<div className="text-primary flex items-start gap-2">
										<span className="shrink-0 pt-1">
											{document.type === 'pdf' ? (
												<FileText size={16} />
											) : (
												<Image size={16} />
											)}
										</span>

										<PreviewFileDialog
											fileKey={field.fileName}
											label={field.fileName.split('/').pop() as string}
										/>
									</div>
									<div className="my-auto flex h-1/2 shrink-0 items-center gap-1">
										<Button
											size={'icon'}
											variant="ghost"
											className={cn('size-8 shrink-0', { hidden: isDisabled })}
											onClick={() => remove(field.originalIndex)}
										>
											<Trash2 className="text-destructive" size={16} />
										</Button>

										{document.type === 'pdf' && (
											<>
												<Separator
													orientation="vertical"
													className="bg-border mx-1 h-3 w-px"
												/>
												<CollapsibleTrigger asChild>
													<Button
														size={'icon'}
														variant={'ghost'}
														className="group size-8 shrink-0"
													>
														<ChevronDown
															size={16}
															className="transition-transform duration-200 group-data-[state=open]:rotate-180"
														/>
													</Button>
												</CollapsibleTrigger>
											</>
										)}
									</div>
								</CardTitle>
							</CardHeader>
							{document.type === 'pdf' ? (
								<CollapsibleContent asChild>
									<CardContent className="flex flex-col gap-2 p-1">
										<InputControl
											name={`files.${field.originalIndex}.documentNumber`}
											placeholder="Masukkan Nomor Dokumen"
										/>
										<div className="flex gap-2">
											<DateControl
												name={`files.${field.originalIndex}.startDate`}
												placeholder="Tanggal Terbit"
												required
												isDisabled={isDisabled}
												calendarProps={{
													captionLayout: 'dropdown',
													endMonth: addYears(new Date(), 40)
												}}
											/>
											<DateControl
												name={`files.${field.originalIndex}.endDate`}
												placeholder="Tanggal Akhir"
												required
												isDisabled={isDisabled}
												calendarProps={{
													captionLayout: 'dropdown',
													endMonth: addYears(new Date(), 40)
												}}
											/>
										</div>
									</CardContent>
								</CollapsibleContent>
							) : null}
						</Collapsible>
					</Card>
				))}
			</div>
		</>
	);
}

function FileUploadPkpNonPkp({
	vendorName,
	documentId,
	isDisabled,
	formId,
	name,
	description,
	visible,
	onOpenChange,
	isPkp,
	files,
	allowApproval,
	vendorDocumentId,
	vendorId,
	defaultValues
}: Readonly<{
	vendorName: string,
	documentId: DocumentId,
	isDisabled: boolean,
	formId: string,
	name: string,
	visible: boolean,
	onOpenChange: (open: boolean) => void,
	description: string | null,
	isPkp: boolean,
	files: VendorDocumentDataResponse['files'],
	allowApproval: boolean,
	vendorDocumentId: number,
	vendorId: number,
	defaultValues: DocumentDataSchema
}>) {
	const document = DOCUMENT[documentId];

	const {
		control,
		reset,
		formState: { isDirty }
	} = useFormContext<DocumentDataSchema>();
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'files'
	});
	const hasReachMaxFiles = fields.length === document.max + (isPkp ? 2 : 0);

	const [progress, setProgress] = useState<number>(0);
	const onOpen = useDialogStore((s) => s.onOpenChange);
	const { askCloseIfDirty, askConfirmDeleteDocument } = useConfirmation();
	const { onError } = useResponse();

	const uploadMutation = useMutation(fileMutations.uploadPresign);

	const dropzone = useDropzone({
		accept:
			document.type === 'image'
				? {
						'image/png': ['.png'],
						'image/jpeg': ['.jpg', '.jpeg']
					}
				: { 'application/pdf': ['.pdf'] },
		disabled: uploadMutation.isPending || isDisabled || hasReachMaxFiles,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			handleUpload(file, 'OTHER');
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({
					code: errFile.errors[0]?.code,
					type: document.type
				}),
				confirmText: 'Coba Lagi'
			});
		}
	});

	function handleUpload(file: File, tag: 'SPPKP' | 'SKT' | 'OTHER') {
		setProgress(0);
		const date = new Date();
		const { fileName, fileExt } = extractFileParts(file.name);
		const fileKey = `${vendorName}/${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${crypto.randomUUID()}/${tag}/${fileName.slice(0, 50)}.${fileExt}`;

		uploadMutation.mutate(
			{
				key: fileKey,
				file,
				onProgress: (val) => setProgress(val)
			},
			{
				onSuccess: () => {
					append({
						fileName: fileKey,
						startDate: null,
						endDate: null,
						documentNumber: null
					});
				},
				onError: noOp
			}
		);
	}

	const documentDescription =
		document.type === 'image'
			? 'Dokumen: JPG, JPEG, PNG (Maks. 5MB per file) dan Unggah satu atau lebih gambar'
			: 'Dokumen: Pdf (Maks. 5MB per file) dan Unggah satu atau lebih dokumen';

	const type = document.type === 'pdf' ? 'Unggah Dokumen' : 'Unggah Gambar';

	const mappedFields = fields.map((f, i) => ({ ...f, originalIndex: i }));

	const sppkpData = mappedFields.find((f) => f.fileName.includes('/SPPKP/'));
	const sktData = mappedFields.find((f) => f.fileName.includes('/SKT/'));

	const otherFiles = mappedFields.filter(
		(f) => !f.fileName.includes('/SPPKP/') && !f.fileName.includes('/SKT/')
	);

	const isFileEmpty = isPkp ? !sppkpData || !sktData : otherFiles.length === 0;

	const buttonText = () => {
		if (isDisabled) {
			return 'Lihat Dokumen';
		}

		if (files.length > 0) {
			return 'Lihat atau unggah ulang dokumen';
		}

		return documentDescription;
	};

	return (
		<Dialog
			open={visible}
			onOpenChange={(v) => {
				if (!v) {
					return askCloseIfDirty(
						isDirty,
						() => {},
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					);
				}

				onOpenChange(true);
			}}
		>
			<div className="mt-2 flex w-fit flex-col gap-2">
				<DialogTrigger asChild>
					<div className="flex flex-col gap-1">
						<Button
							variant={'outline'}
							className="border-input flex h-auto min-h-12 w-full items-start py-2 text-start"
							type="button"
						>
							<img
								src="/icon/upload.webp"
								alt="icon-upload"
								className="mt-0.5 size-4 shrink-0"
							/>
							<span className="flex flex-col items-start justify-start">
								<p className="text-xs text-black">
									{files.length > 0 || allowApproval
										? `Lihat Dokumen (${files.length})`
										: type}
								</p>
								<p className="text-secondary items-star flex justify-start text-justify text-xs leading-tight font-light whitespace-normal">
									{buttonText()}
								</p>
							</span>
						</Button>
						{isFileEmpty ? (
							<p
								data-slot="form-description"
								className={cn('text-destructive text-sm')}
							>
								* Wajib diisi
							</p>
						) : null}
					</div>
				</DialogTrigger>
			</div>
			<DialogContent
				className="flex max-h-[90vh] flex-col bg-white"
				onPointerDownOutside={(e) =>
					askCloseIfDirty(
						isDirty,
						() => e.preventDefault(),
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					)
				}
				onEscapeKeyDown={(e) =>
					askCloseIfDirty(
						isDirty,
						() => e.preventDefault(),
						() => {
							onOpenChange(false);
							onOpen(false);
							reset(defaultValues);
						}
					)
				}
			>
				<DialogTitle className="flex shrink-0 flex-col gap-1">
					<span className="text-sm font-bold text-black">{name}</span>
					<span className="text-label-input text-xs">{description}</span>
				</DialogTitle>
				<div className="flex flex-1 flex-col gap-2 overflow-y-auto">
					{isPkp ? (
						<>
							<SingleSlotUpload
								field={sppkpData}
								isDisabled={isDisabled}
								isPending={uploadMutation.isPending}
								label="Surat SPPKP"
								onRemove={() =>
									askConfirmDeleteDocument(() => {
										remove(sppkpData?.originalIndex);
										onOpen(false);
									})
								}
								onUpload={handleUpload}
								tag="SPPKP"
							/>
							<SingleSlotUpload
								field={sktData}
								isDisabled={isDisabled}
								isPending={uploadMutation.isPending}
								label="Surat SKT"
								onRemove={() =>
									askConfirmDeleteDocument(() => {
										remove(sktData?.originalIndex);
										onOpen(false);
									})
								}
								onUpload={handleUpload}
								tag="SKT"
							/>
						</>
					) : null}
					<MultipleSlotUpload
						{...dropzone}
						remove={(index) =>
							askConfirmDeleteDocument(() => {
								remove(index);
								onOpen(false);
							})
						}
						isDisabled={isDisabled}
						document={document}
						isPending={uploadMutation.isPending}
						progress={progress}
						hasReachMaxFiles={hasReachMaxFiles}
						fields={otherFiles}
						label="Unggah Dokumen Lainnya"
					/>
				</div>
				{isDisabled ? null : (
					<DialogFooter className="shrink-0 sm:justify-end">
						<Button
							type="submit"
							disabled={uploadMutation.isPending || !isDirty}
							form={formId}
						>
							Simpan
						</Button>
					</DialogFooter>
				)}
				{allowApproval ? (
					<ApprovalDocument
						vendorDocumentId={vendorDocumentId}
						vendorId={vendorId}
						next={() => onOpenChange(false)}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function PkpNonPkp({
	value,
	isDisabled,
	vendorName,
	files,
	description,
	name,
	visible,
	onOpenChange,
	allowApproval,
	vendorDocumentId,
	vendorId
}: Readonly<{
	value?: string,
	isDisabled: boolean,
	vendorName: string,
	files: VendorDocumentDataResponse['files'],
	name: string,
	description: string | null,
	visible: boolean,
	onOpenChange: (open: boolean) => void,
	allowApproval: boolean,
	vendorDocumentId: number,
	vendorId: number
}>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const formId = useId();
	const submit = useMutation(vendorMutations.documentDataUpdate);

	const { onSuccess, onError } = useResponse();

	const handleSubmit = (
		data: StrictOmit<DocumentDataSchema, 'documentId'>,
		type: 'file' | 'option'
	) => {
		submit.mutate(
			{
				...data,
				documentId: 'D002',
				files: data.files.map((file) => ({
					...file,
					startDate: file.startDate ? format(file.startDate, 'yyyy-MM-dd') : null,
					endDate: file.endDate ? format(file.endDate, 'yyyy-MM-dd') : null
				}))
			},
			{
				onSuccess: () => {
					if (type === 'file') {
						onSuccess({ message: 'Dokumen Anda berhasil di unggah!' });
						onOpenChange(false);
					} else {
						onOpen(false);
					}
				},
				onError: ({ message }) => onError({ message })
			}
		);
	};

	return (
		<div className="flex flex-1 grow flex-col items-start justify-between">
			<RadioGroup
				value={value}
				disabled={isDisabled}
				onValueChange={(value) => {
					const isNonPkp = value === 'non-pkp';
					const hasSppkpOrSkt = files.some(
						(f) => f.fileName.includes('/SPPKP/') || f.fileName.includes('/SKT/')
					);

					if (isNonPkp && hasSppkpOrSkt) {
						setDialog('variant', 'warning');
						setDialog('title', 'Ubah PKP?');
						setDialog(
							'description',
							'Mengubah opsi akan menghapus file SPPKP atau SKT yang sudah diunggah'
						);
						setDialog('confirmText', 'Ya, Ubah');
						setDialog('cancelText', 'Batal');
						setAction('onConfirm', () =>
							handleSubmit(
								{
									files: files.filter(
										(f) =>
											!f.fileName.includes('/SPPKP/') && !f.fileName.includes('/SKT/')
									),
									value
								},
								'option'
							)
						);
						setAction('onCancel', () => onOpen(false));
						onOpen(true);
						return;
					}

					handleSubmit({ files, value }, 'option');
				}}
				className="flex flex-row"
			>
				<div className="flex space-x-2">
					<RadioGroupItem value={'pkp'} id={'pkp'} />
					<Label htmlFor={'pkp'}>PKP</Label>
				</div>
				<div className="flex space-x-2">
					<RadioGroupItem value="non-pkp" id="non-pkp" />
					<Label htmlFor="non-pkp">Non PKP</Label>
				</div>
			</RadioGroup>
			<HookFormProvider
				schema={documentDataSchema}
				defaultValues={{
					files,
					documentId: 'D002',
					value
				}}
				onSubmit={(data: DocumentDataSchema) => handleSubmit(data, 'file')}
				id={formId}
				key={formId}
				formOptions={{ disabled: isDisabled }}
			>
				<FileUploadPkpNonPkp
					documentId="D002"
					vendorName={vendorName}
					isDisabled={isDisabled}
					formId={formId}
					name={name}
					description={description}
					visible={visible}
					onOpenChange={onOpenChange}
					isPkp={value === 'pkp'}
					key={formId}
					files={files}
					allowApproval={allowApproval}
					vendorDocumentId={vendorDocumentId}
					vendorId={vendorId}
					defaultValues={{
						files,
						documentId: 'D002',
						value
					}}
				/>
			</HookFormProvider>
		</div>
	);
}

export function Document({
	name,
	description,
	files,
	required,
	value,
	id,
	isDisabled = false,
	vendorName,
	status,
	statusId,
	allowApproval = false,
	isApproval = false,
	vendorDocumentId,
	vendorId,
	history
}: VendorDocumentDataResponse & {
	isDisabled?: boolean,
	vendorName: string,
	allowApproval?: boolean,
	isApproval?: boolean
}) {
	const formId = useId();
	const submit = useMutation(vendorMutations.documentDataUpdate);

	const [visible, setVisible] = useState(false);

	const { onSuccess, onError } = useResponse();

	const handleVisibleDialog = (open: boolean) => setVisible(open);

	const handleSubmit = (data: DocumentDataSchema) => {
		const document = DOCUMENT[data.documentId];

		submit.mutate(
			{
				...data,
				files: data.files.map((file) => ({
					...file,
					...(document.type === 'pdf' && {
						documentNumber: file.documentNumber,
						startDate: file.startDate ? format(file.startDate, 'yyyy-MM-dd') : null,
						endDate: file.endDate ? format(file.endDate, 'yyyy-MM-dd') : null
					})
				}))
			},
			{
				onSuccess: () => {
					onSuccess({ message: 'Dokumen Anda berhasil di unggah!' });
					handleVisibleDialog(false);
				},
				onError: ({ message }) => onError({ message })
			}
		);
	};

	return (
		<Card className="flex h-full flex-col p-1">
			<CardHeader className="flex flex-col gap-2 p-2">
				{statusId === StatusDocument.DraftDocument ? null : (
					<Badge
						className="px-4 py-1.5"
						style={{
							color: status.text_color,
							backgroundColor: status.text_background_color
						}}
					>
						{status.text}
					</Badge>
				)}
				<CardTitle className="text-xs md:text-sm">
					{name} {required ? <span className="text-red">*</span> : null}
				</CardTitle>
				<CardDescription className="text-xs md:text-sm">
					{description}
				</CardDescription>
			</CardHeader>
			<CardContent
				className={cn('flex flex-1 p-2', {
					'items-end': !['D001', 'D002'].includes(id)
				})}
			>
				{id === 'D001' ? (
					<AktaPerubahaan
						value={value ?? undefined}
						files={files}
						isDisabled={isDisabled}
						vendorName={vendorName}
						name={name}
						description={description}
						visible={visible}
						onOpenChange={handleVisibleDialog}
						allowApproval={allowApproval}
						vendorDocumentId={vendorDocumentId}
						vendorId={vendorId}
						isApproval={isApproval}
					/>
				) : null}
				{id === 'D002' ? (
					<PkpNonPkp
						value={value ?? undefined}
						isDisabled={isDisabled}
						vendorName={vendorName}
						files={files}
						name={name}
						description={description}
						visible={visible}
						onOpenChange={handleVisibleDialog}
						allowApproval={allowApproval}
						vendorDocumentId={vendorDocumentId}
						vendorId={vendorId}
					/>
				) : null}
				{['D001', 'D002'].includes(id) ? null : (
					<HookFormProvider
						schema={documentDataSchema}
						defaultValues={{
							files,
							documentId: id
						}}
						onSubmit={handleSubmit}
						id={formId}
						key={formId}
						formOptions={{ disabled: isDisabled }}
						className="w-full"
					>
						<FileUpload
							documentId={id}
							vendorName={vendorName}
							isDisabled={isDisabled}
							formId={formId}
							key={formId}
							name={name}
							description={description}
							visible={visible}
							onOpenChange={handleVisibleDialog}
							files={files}
							allowApproval={allowApproval}
							vendorDocumentId={vendorDocumentId}
							vendorId={vendorId}
							defaultValues={{
								files,
								documentId: id
							}}
						/>
					</HookFormProvider>
				)}
			</CardContent>
			{history ? (
				<CardFooter className="w-full flex-1 p-2">
					<Card className="w-full rounded-sm border border-red-500 bg-rose-100 p-1">
						<CardContent className="flex flex-col gap-1 p-1 text-xs">
							<span className="flex flex-row items-center gap-2">
								<Info className="text-red-500" size={14} />
								Alasan Ditolak:
							</span>
							<p>{history.remarks}</p>
						</CardContent>
					</Card>
				</CardFooter>
			) : (
				<div className="flex-1" />
			)}
		</Card>
	);
}

export default function FormDocument({
	isDisabled = false,
	vendorName
}: Readonly<{
	isDisabled?: boolean,
	vendorId: number,
	vendorName: string
}>) {
	const document = useQuery(vendorQueries.documentData());

	return (
		<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
			<WithStateQuery state={document} key={document.dataUpdatedAt}>
				{({ data }) =>
					data.map((doc) => (
						<Document
							{...doc}
							key={doc.id}
							isDisabled={isDisabled}
							vendorName={vendorName}
						/>
					))
				}
			</WithStateQuery>
		</div>
	);
}
